import { memo, useEffect, useMemo } from 'react';

import {
  MapboxOverlay as DeckOverlay,
  MapboxOverlayProps,
} from '@deck.gl/mapbox';
import { useTranslation } from 'react-i18next';
import { useControl } from 'react-map-gl';
import { useSnapshot } from 'valtio';

import { store } from '../../store';

import { berthLayer } from './BerthLayer';
import { geofenceLayer } from './GeofenceLayer';
import { gpsLayer } from './GpsLayer';
import { iconLayer } from './IconLayer';
import { tripIconLayer } from './TripIconLayer';
import { tripLayer } from './TripLayer';

import 'mapbox-gl/dist/mapbox-gl.css';

function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl(() => new DeckOverlay(props) as any);
  overlay.setProps(props);
  return null;
}

const DeckGlLayersComponent = ({ mapRef }: { mapRef: any }) => {
  const { berths, geofences } = useSnapshot(store.mapLayers);
  const { filtered, highlightedIndex } = useSnapshot(store.gpsData);
  const { t } = useTranslation();

  const GEOFENCE_LABELS: Record<string, string> = {
    DRY_STORAGE: 'Dry storage',
    REEFER_STORAGE: 'Reefer storage',
    CUSTOM: 'Custom',
    PHYTO: 'Phyto',
    RAIL_TERMINAL: 'Rail terminal',
    LOADING_QUAY: 'Loading quay',
  };

  // This layer updates when 'filtered' or 'highlightedIndex' changes.
  // Ensure 'iconLayer' itself uses 'updateTriggers' internally for props affected by 'highlightedIndex'.
  // See the example above for how to set updateTriggers in your IconLayer.ts file.
  const _iconLayer = useMemo(() => {
    // console.log('Recalculating _iconLayer. highlightedIndex:', highlightedIndex); // Keep original logging level
    return iconLayer(filtered, highlightedIndex);
  }, [filtered, highlightedIndex]);

  const _tripLayer = useMemo(() => {
    return tripLayer(filtered);
  }, [filtered]);

  // This layer updates only when 'filtered' changes.
  const _tripIconLayer = useMemo(() => {
    return tripIconLayer(filtered);
  }, [filtered]);

  // This layer updates only when 'mapRef' changes.
  const _gpsLayer = useMemo(() => {
    return gpsLayer(filtered);
  }, [filtered]);

  // This layer updates only when 'berths' data changes.
  const _berthLayer = useMemo(() => berthLayer(berths), [berths]);

  const _geofenceLayer = useMemo(() => geofenceLayer(geofences), [geofences]);

  const layers = [
    _geofenceLayer,
    _berthLayer,
    _gpsLayer,
    _iconLayer,
    _tripIconLayer,
    _tripLayer,
  ];

  const getTooltip = ({ object }: { object: any }) => {
    if (object && object.type) {
      const label = t(`layers.${object.type.toLowerCase()}`, {
        defaultValue: GEOFENCE_LABELS[object.type] || object.type,
      });
      return { text: label };
    }
    return null;
  };

  return (
    <DeckGLOverlay
      //@ts-ignore
      initialViewState={{
        longitude: -122.4,
        latitude: 37.74,
        zoom: 3,
      }}
      debug={true} // Enables Deck.gl debugging information in the console
      controller
      layers={layers}
      getTooltip={getTooltip as any}
    />
  );
};

// Temporarily use the component directly without React.memo for testing
export const DeckGlLayers = DeckGlLayersComponent;

/*
// Original code with React.memo:
export const DeckGlLayers = memo(
  DeckGlLayersComponent,
  (prevProps: any, nextProps: any) => {
    // Ne re-rendre que si mapRef change
    return prevProps.mapRef === nextProps.mapRef;
  },
);
*/
